'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { cn } from '@/core/utils/common'

import { openNavDrawer } from '@/features/veluvit/components/shell/nav-drawer'
import {
  activeTab,
  BOTTOM_NAV,
} from '@/features/veluvit/components/shell/nav-items'
import TabIcon, {
  type TabKey,
} from '@/features/veluvit/components/shell/tab-icon'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

/* 줄어드는 연출 — 알약 높이 · 칸 폭 · 아이콘 위치 · 이름이 모두 같은 시간 · 같은 곡선으로 움직여야 한 덩어리로 보인다.
   예전엔 이름을 max-height 로 접어(글자가 위아래로 잘림) 아이콘이 알약 높이와 다른 박자로 내려와 뚝뚝 끊겼다(2026-10-15).
   이름은 바닥에 붙여 두고 투명도 · 위치만, 아이콘은 위치(translate)만 움직인다 */
const MOTION = 'duration-[320ms] ease-smooth'

/** 칸 하나 — 아이콘 칩 + 짧은 이름. 내리면(mini) 이름이 사라지고 칸이 좁아진다 */
const ITEM = cn(
  'group relative flex h-full flex-col items-center justify-center rounded-panel no-underline transition-[width,color]',
  MOTION
)

/** 선택 표시는 아이콘 채움(TabIcon) + 밝은 글자만 — 아이콘 뒤 회색 칩은 뺐다(2026-10-15) */
function Cell({ k, label, mini }: { k: TabKey; label: string; mini: boolean }) {
  return (
    <>
      {/* 펼친 상태에선 이름 자리만큼 위로 — 아이콘 칸(30) + 틈(3) + 이름(13)이 알약 높이 66 가운데에 온다 */}
      <span
        className={cn(
          'grid h-7.5 w-11 place-items-center transition-[translate]',
          MOTION,
          mini ? 'translate-y-0' : '-translate-y-2'
        )}
      >
        <TabIcon k={k} />
      </span>
      <span
        aria-hidden={mini || undefined}
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-2.5 text-center text-c11 leading-[1.2] font-semibold whitespace-nowrap transition-[opacity,translate]',
          MOTION,
          mini ? 'translate-y-1.5 opacity-0' : 'translate-y-0 opacity-100'
        )}
      >
        {label}
      </span>
    </>
  )
}

/**
 * 모바일 하단 탭(≤1000px) — 떠 있는 유리 알약. 맨 왼쪽 ☰ 은 메뉴 서랍(미션 · 스튜디오 …)을 열고, 나머지는 BOTTOM_NAV.
 * 스크롤을 내리면 이름이 사라지며 줄어든다(app-shell 이 tabMini 를 잡는다). PC 는 상단 내비(gnb)가 맡는다.
 * 높이는 이 알약에만 둔다 — 화면 틀(#screen)의 CSS 변수로 바꾸면 페이지 전체가 스타일을 다시 계산해 줄어드는 첫 프레임이 걸렸다.
 */
export default function BottomTabs() {
  const pathname = usePathname()
  const mini = useUiStore((s) => s.tabMini)
  const k = activeTab(pathname)
  // 메뉴 소속이 없거나 스튜디오(편집 화면이 하단을 쓴다) · 탐색(화면 가득 릴스 — 쇼츠처럼 탭 없이, 2026-10-08)이면 숨긴다
  const hidden = !k || k === 'studio' || k === 'explore'

  return (
    <nav
      aria-label="주요 메뉴"
      className={cn(
        'absolute bottom-[calc(10px_+_env(safe-area-inset-bottom,0px))] left-1/2 z-50 max-w-[calc(100%_-_24px)] -translate-x-1/2 items-center gap-0.5 rounded-full border border-foreground/8 bg-well/55 px-1.5 shadow-[0_8px_30px_rgb(0_0_0/0.45),inset_0_1px_0_rgb(255_255_255/0.08)] backdrop-blur-2xl backdrop-saturate-[1.8] transition-[height]',
        MOTION,
        mini ? 'h-13' : 'h-16.5',
        // 표시 방식은 한 클래스만 — cn() 이 병합하지 않아 flex 와 hidden 을 같이 두면 어느 쪽이 이길지 모른다
        hidden ? 'hidden' : 'flex over10:hidden'
      )}
    >
      <Button
        type="button"
        aria-label="전체 메뉴"
        onClick={openNavDrawer}
        className={cn(
          ITEM,
          'cursor-pointer text-t3 hover:text-t1',
          mini ? 'w-13' : 'w-15.5'
        )}
        variant="bare"
      >
        <Cell k="menu" label="메뉴" mini={mini} />
      </Button>
      {/* 메뉴 칸과 페이지 칸 사이 가는 세로선 — ☰ 은 이동이 아니라 여는 버튼이라 한 칸 떼어 둔다 */}
      <span
        aria-hidden="true"
        className="mx-0.5 h-6 w-px flex-none bg-foreground/10"
      />
      {BOTTOM_NAV.map((tab) => {
        const on = tab.k === k
        return (
          <Link
            key={tab.k}
            href={tab.href}
            aria-label={tab.label}
            aria-current={on ? 'page' : undefined}
            className={cn(
              ITEM,
              on ? 'text-t1' : 'text-t3 hover:text-t1',
              mini ? 'w-13' : 'w-15.5'
            )}
          >
            <Cell k={tab.k} label={tab.short ?? tab.label} mini={mini} />
          </Link>
        )
      })}
    </nav>
  )
}
